import { Text } from '@/components/shared/element/text';
import { styled } from '@/stitches.config';
import { IMy24Theme } from '@/types/shared';

const StyledContent = styled('div', {
  '& sup': {
    lineHeight: '0px',
  },
});

const ContentDescription = styled(Text, {
  '& h3': {
    fontFamily: '$bold',
    fontSize: `clamp(18px, ${-360 * ((20 - 18) / 920) + 18}px + ${
      ((20 - 18) / 920) * 100
    }vw, 20px)`,
    fontStyle: 'normal',
    fontWeight: 'normal',
    lineHeight: '125%',
    margin: '$space-0',
    '& ~ p, & ~ ul': {
      marginTop: '0.5em',
    },
  },
  '& small': {
    display: 'block',
    fontSize: 'calc(14 / 18 * 100%)',
  },
});

const Divider = styled('hr', {
  width: 40,
  height: 4,
  bc: '$primary',
  mx: '$space-0',
  my: '$space-4',
  border: 0,
  transform: 'skewX(-21deg)',
  transformOrigin: 'bottom',
  '@sm': {
    my: '$space-5',
  },
  '@lg': {
    my: '$space-4',
    width: 48,
  },
  '@xl': {
    my: '$space-5',
  },
});

interface IProps extends React.ComponentProps<typeof StyledContent> {
  preTitle?: string;
  excerpt?: string;
  title?: string;
  description?: string;
  theme?: IMy24Theme;
  small?: boolean;
  descriptionTheme?: 'light' | 'dark';
  preTitleColor?: 'red' | 'blue';
  isLine?: boolean;
  dataTest?: string;
  align?: 'center' | 'left' | 'right';
}

export const Content: React.FC<IProps> = ({
  theme = 'light',
  small = false,
  dataTest = 'content',
  align = 'left',
  preTitleColor = 'red',
  ...props
}) => {
  const titleVariant = () => {
    switch (theme) {
      case 'light':
        return 'text-gray-darkest';
      case 'dark':
        return 'text-white';
      default:
        return 'text-gray-darkest';
    }
  };

  const descriptionVariant = () => {
    switch (theme) {
      case 'light':
        if (props?.descriptionTheme === 'light') return 'text-gray';
        return 'text-gray-darkest';
      case 'dark':
        return 'text-white';
      default:
        return 'text-gray-darkest';
    }
  };

  const preTitleVariant = () => {
    switch (theme) {
      case 'light':
        if (preTitleColor === 'blue') return 'text-blue';
        return 'text-red';
      case 'dark':
        if (preTitleColor === 'blue') return 'text-blue';
        return 'text-red';
      default:
        return 'text-red';
    }
  };

  return (
    <StyledContent css={props?.css} className={props?.className}>
      {!!props?.preTitle && (
        <Text
          as="h3"
          size={small ? 'text-body-2' : 'text-body-1'}
          font="bold"
          variant={preTitleVariant()}
          wrap
          dangerouslySetInnerHTML={{
            __html: props?.preTitle,
          }}
          data-test={`${dataTest}_item_preTitle`}
        />
      )}
      {!!props?.excerpt && (
        <Text
          as="h3"
          size={small ? 'text-sub-h2' : 'text-sub-h1'}
          font="bold"
          variant={titleVariant()}
          wrap
          dangerouslySetInnerHTML={{
            __html: props?.excerpt,
          }}
          data-test={`${dataTest}_item_excerpt`}
          css={{ marginBottom: '$space-3' }}
        />
      )}
      {!!props?.title && (
        <Text
          as="h3"
          size={small ? 'text-body-1' : 'text-sub-h2'}
          font="bold"
          variant={titleVariant()}
          wrap
          dangerouslySetInnerHTML={{
            __html: props?.title,
          }}
          data-test={`${dataTest}_item_title`}
          css={{
            '@lg': {
              textAlign: align,
            },
          }}
        />
      )}
      {!!props?.isLine && <Divider />}
      {!!props?.description && (
        <ContentDescription
          as="div"
          size={small ? 'text-body-2' : 'text-body-1'}
          variant={descriptionVariant()}
          wrap
          dangerouslySetInnerHTML={{
            __html: props?.description,
          }}
          data-test={`${dataTest}_item_description`}
          css={{
            marginTop: props?.title && '$space-2',
            '@lg': {
              textAlign: align,
            },
          }}
        />
      )}
    </StyledContent>
  );
};

Content.displayName = 'SharedMY24Content';
